import {
  AnalyticsEvent,
  SdScrollableScreen,
  SdScrollableScreenBodyChildResult,
  SdScrollableScreenFooterChildResult,
  Typenames,
} from '@varo/graphql-schema-types';
import iconFullWidthTextComponent from 'features/directDepositBenefits/DirectDepositBenefitsScreen/adapters/iconFullWidthTextComponent';
import sdColorBackgroundContainer from 'features/directDepositBenefits/DirectDepositBenefitsScreen/adapters/sdColorBackgroundContainer';
import { ListItemProps } from 'shared/components/vxl/ListItem/ListItem';
import buttonComponent, {
  ButtonComponentDependencies,
} from 'shared/legacySDUIAdapters/buttonComponent';
import fullWidthTextComponent from 'shared/legacySDUIAdapters/fullWidthTextComponent';
import htmlComponent, {
  HtmlComponentDependencies,
} from 'shared/legacySDUIAdapters/htmlComponent';
import vxlHairline from 'shared/legacySDUIAdapters/vxlHairline';
import vxlSpacing, {
  SPACER_PREFIX,
} from 'shared/legacySDUIAdapters/vxlSpacing';
import { VXLComponentId, VXLGroupId, VXLNode } from 'shared/renderer/types';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';
import { Maybe } from 'shared/utils/types';

export interface DirectDepositBenefitsDependencies
  extends ButtonComponentDependencies,
    HtmlComponentDependencies {}

export interface DirectDepositBenefitsContent {
  body: VXLNode[];
  footer: Maybe<VXLNode[]>;
  displayAnalytics?: Maybe<AnalyticsEvent>;
}

const directDepositBenefitsAdapter = (
  element:
    | SdScrollableScreenBodyChildResult
    | SdScrollableScreenFooterChildResult,
  dependencies: DirectDepositBenefitsDependencies,
  index: number,
): Maybe<VXLNode> => {
  switch (element.__typename) {
    case Typenames.SDColorBackgroundContainer:
      return sdColorBackgroundContainer(element);
    case Typenames.SpacingComponent:
      return vxlSpacing(element, index);
    case Typenames.FullWidthTextComponent:
      return fullWidthTextComponent(element);
    case Typenames.IconFullWidthTextComponent:
      return iconFullWidthTextComponent(element);
    case Typenames.VXLHairlineComponent:
      return vxlHairline(element);
    case Typenames.HtmlComponent:
      return htmlComponent(element, dependencies);
    case Typenames.ButtonComponent:
      return buttonComponent(element, dependencies);
    default:
      return null;
  }
};

const isListItem = (node: VXLNode): boolean => {
  if ('name' in node && node.name === VXLComponentId.LIST_ITEM) {
    return true;
  }
  return false;
};

const isSpacer = (node: VXLNode): boolean => {
  if ('id' in node && node.id && node.id.toString().includes(SPACER_PREFIX)) {
    return true;
  }
  return false;
};

/**
 * this function is used to flatten consecutive list items into
 * a group of list items. it selects list items with one or no
 * spacing elements between them and compresses them into a single
 * list item group node. it returns a new, modified array.
 */
const groupListItems = (nodes: VXLNode[]) => {
  const newNodes: VXLNode[] = [];
  let activeGroup: ListItemProps[] | null = null;

  nodes.forEach((node, index) => {
    if (isListItem(node)) {
      if (activeGroup) {
        activeGroup.push(node.props as ListItemProps);
      } else {
        activeGroup = [node.props as ListItemProps];
      }
    } else if (
      !isSpacer(node) ||
      !(nodes[index + 1] && isListItem(nodes[index + 1]))
    ) {
      if (activeGroup) {
        newNodes.push({
          id: `list-group-${index}`,
          name: VXLGroupId.LIST_ITEM_GROUP,
          props: { items: activeGroup },
        });
        activeGroup = null;
      }

      newNodes.push(node);
    }
  });
  return newNodes;
};

const formatDirectDepositBenefitsContent = (
  data: SdScrollableScreen,
  dependencies: DirectDepositBenefitsDependencies,
): DirectDepositBenefitsContent => {
  // Top bar color is ignored because we have a new custom header.
  const { body: rawBody, footer: rawFooter, displayAnalytics } = data;

  const body: VXLNode[] = rawBody
    .map((element, index) => {
      return directDepositBenefitsAdapter(element, dependencies, index);
    })
    .filter(isNotNil);

  const footer: Maybe<VXLNode[]> = rawFooter
    ?.map((element, index) => {
      return directDepositBenefitsAdapter(element, dependencies, index);
    })
    .filter(isNotNil);

  return {
    body: groupListItems(body),
    footer,
    displayAnalytics,
  };
};

export default formatDirectDepositBenefitsContent;
